import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Pasadena", cities: "Altadena, San Rafael Hills, Linda Vista, Chapman Woods" },
    { zone: "Arcadia", cities: "Arcadia, Temple City, El Monte, Baldwin Park" },
    { zone: "San Marino", cities: "San Marino, Alhambra, San Gabriel, Rosemead" },
    { zone: "Monrovia", cities: "Monrovia, Duarte, Azusa, Glendora" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos Cerca de Pasadena</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el Valle de San Gabriel.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Pasadena</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const PasadenaSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "pasadena", name: "Pasadena", type: "city", regionSlug: "los-angeles" }}>
      <SEO
        title="Limpieza del Hogar en Pasadena, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Pasadena, Arcadia, San Marino y Monrovia. Precios planos, reserva en 60 segundos, garantía del 200%. ¡Pide tu precio ahora!"
        keywords="limpieza del hogar pasadena, servicio de limpieza pasadena ca, limpieza profunda pasadena, empleada doméstica pasadena, limpieza de casas san gabriel valley, maid service pasadena español, limpieza arcadia san marino"
        url="https://uplevelcleaning.com/es/pasadena"
        locationName="Pasadena"
        regionSlug="los-angeles"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "Los Ángeles (Español)", url: "/es/los-angeles" },
          { name: "Pasadena", url: "/es/pasadena" }
        ]}
        reviews={[
          { author: "Gabriela N.", rating: 5, reviewBody: "Vivo en una casa histórica y necesito limpieza especializada. Uplevel Cleaning trató mi hogar con el cuidado que merece. ¡Fantástico!", datePublished: "2026-03-14" },
          { author: "Fernando C.", rating: 5, reviewBody: "Servicio puntual, precio exacto y resultados impecables. Ya los contraté para servicio quincenal. ¡Muy recomendados!", datePublished: "2026-02-18" },
          { author: "Lorena H.", rating: 5, reviewBody: "El equipo fue muy amable y respetuoso con mis pertenencias. Dejaron la casa perfecta para recibir visitas esa misma tarde.", datePublished: "2026-01-25" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Pasadena" regionSlug="los-angeles" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {["Servicio Local en Pasadena", "Casas Históricas Bienvenidas", "Garantía del 200%", "Precios Transparentes", "Reserva en 60 Segundos"].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Pasadena" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="pasadena" />
          <Guarantee />
          <SpanishFAQ cityName="Pasadena" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Pasadena" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default PasadenaSpanishPage;
